import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Typography,
  Box,
  TablePagination,
  TableSortLabel,
  alpha,
  useTheme,
  Icon,
  Paper,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";

export interface Column<T> {
  id: string;
  label: string;
  minWidth?: number;
  maxWidth?: number;
  align?: "left" | "right" | "center";
  sortable?: boolean;
  format?: (value: any, row: T) => React.ReactNode;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  totalItems: number;
  page: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  onPageChange: (newPage: number) => void;
  onPageSizeChange: (newPageSize: number) => void;
  onSort?: (field: string, order: "asc" | "desc") => void;
  onDetails?: (row: T) => void;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  loading?: boolean;
  getRowId: (row: T) => string | number;
  additionalActions?: (row: T) => React.ReactNode;
}

export function DataTable<T>({
  columns,
  rows,
  totalItems,
  page,
  pageSize,
  sortField,
  sortOrder = "asc",
  onPageChange,
  onPageSizeChange,
  onSort,
  onDetails,
  onEdit,
  onDelete,
  loading = false,
  getRowId,
  additionalActions,
}: DataTableProps<T>) {
  const theme = useTheme();

  const handleSort = (field: string) => {
    if (!onSort) return;
    const newOrder =
      sortField === field && sortOrder === "asc" ? "desc" : "asc";
    onSort(field, newOrder);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleChangePageSize = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    onPageSizeChange(value);
  };

  const renderCellContent = (column: Column<T>, row: T): React.ReactNode => {
    if (column.format) {
      return column.format(row[column.id as keyof T], row);
    }
    const value = row[column.id as keyof T];
    if (value === null || value === undefined) return "-";
    if (typeof value === "string" || typeof value === "number") return value;
    return JSON.stringify(value);
  };

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        width: "100%",
        overflow: "hidden",
        borderRadius: 2,
        bgcolor: "background.paper",
      }}
    >
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || "left"}
                  style={{
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {column.sortable && onSort ? (
                    <TableSortLabel
                      active={sortField === column.id}
                      direction={sortField === column.id ? sortOrder : "asc"}
                      onClick={() => handleSort(column.id)}
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        "& .MuiTableSortLabel-icon": {
                          color: "primary.main",
                        },
                      }}
                    >
                      {column.label}
                      {sortField === column.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {sortOrder === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  ) : (
                    <Typography
                      variant="subtitle2"
                      component="span"
                      sx={{ fontWeight: 600 }}
                    >
                      {column.label}
                    </Typography>
                  )}
                </TableCell>
              ))}
              <TableCell
                align="right"
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                  borderTop: `1px solid ${theme.palette.divider}`,
                  minWidth: 120,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Actions
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                hover
                key={getRowId(row)}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  transition: "all 0.2s",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                  },
                }}
              >
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={{
                      maxWidth: column.maxWidth,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {renderCellContent(column, row)}
                  </TableCell>
                ))}
                <TableCell align="right">
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      justifyContent: "flex-end",
                    }}
                  >
                    {additionalActions && additionalActions(row)}
                    {onDetails && (
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => onDetails(row)}
                          sx={{
                            color: "primary.main",
                            bgcolor: (theme) =>
                              alpha(theme.palette.primary.main, 0.1),
                            "&:hover": {
                              bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.2),
                              transform: "translateY(-2px)",
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <Icon>visibility</Icon>
                        </IconButton>
                      </Tooltip>
                    )}
                    {onEdit && (
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(row)}
                          sx={{
                            color: "info.main",
                            bgcolor: (theme) =>
                              alpha(theme.palette.info.main, 0.1),
                            "&:hover": {
                              bgcolor: (theme) =>
                                alpha(theme.palette.info.main, 0.2),
                              transform: "translateY(-2px)",
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <Icon>edit</Icon>
                        </IconButton>
                      </Tooltip>
                    )}
                    {onDelete && (
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => onDelete(row)}
                          sx={{
                            color: "error.main",
                            bgcolor: (theme) =>
                              alpha(theme.palette.error.main, 0.1),
                            "&:hover": {
                              bgcolor: (theme) =>
                                alpha(theme.palette.error.main, 0.2),
                              transform: "translateY(-2px)",
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <Icon>delete</Icon>
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalItems}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={pageSize}
        onRowsPerPageChange={handleChangePageSize}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      />
    </Paper>
  );
}
